/**
 * Route Update Configuration
 *
 * Centralized configuration for PWA update behavior across routes.
 * This is the SINGLE SOURCE OF TRUTH for all update behavior.
 */

export interface RouteUpdateConfig {
	path: string;
	allowUpdates: boolean;
	silentUpdate: boolean;
	description: string;
}

/**
 * Master list of route update configurations
 *
 * IMPORTANT: This is the SINGLE SOURCE OF TRUTH for PWA update behavior.
 *
 * Update Behavior:
 * - allowUpdates: true = Route allows update application
 * - silentUpdate: true = Apply updates without notification
 * - silentUpdate: false = Show snackbar notification with "Update Now" button
 *
 * Routes NOT listed here will automatically defer updates until user navigates to a configured route.
 * Examples of deferred routes: /attempt, /sign-in-kiosk/sign-in, /oauth, etc.
 *
 * Organization:
 * - Sorted by specificity (more specific paths first)
 * - Grouped by update type (notification vs silent)
 */
export const ROUTE_UPDATE_CONFIG: RouteUpdateConfig[] = [
	// Routes with USER NOTIFICATION (silentUpdate: false)
	// Users see snackbar with "Update Now" button
	{
		path: "company",
		allowUpdates: true,
		silentUpdate: false,
		description: "Company portal - Show notification to give users control over update timing",
	},
	{
		path: "incident-reporting/:seoName",
		allowUpdates: true,
		silentUpdate: false,
		description: "Incident reporting - Show notification to prevent data loss during reporting",
	},

	// Routes with SILENT UPDATES (silentUpdate: true)
	// Updates applied without user notification

	// Portal routes - more specific first
	{
		path: "portal/:seoName/inductee/special-instructions",
		allowUpdates: true,
		silentUpdate: true,
		description: "Inductee portal - Special instructions page (safe landing page)",
	},
	{
		path: "portal/:seoName/inductee/inductions",
		allowUpdates: true,
		silentUpdate: true,
		description: "Inductee portal - Inductions list (safe landing page for silent updates)",
	},
	{
		path: "company-web-portal",
		allowUpdates: true,
		silentUpdate: true,
		description: "External company portal - Silent update for seamless experience",
	},
	{
		path: "sign-in-kiosk/start",
		allowUpdates: true,
		silentUpdate: true,
		description: "Kiosk landing page - Silent update (safe zone, no active workflow)",
	},
	{
		path: "inductee",
		allowUpdates: true,
		silentUpdate: true,
		description: "Inductee portal - Silent update to avoid interrupting induction workflow",
	},
	{
		path: "auth",
		allowUpdates: true,
		silentUpdate: true,
		description: "Authentication - Silent update (auth is transient, user won't notice)",
	},
];

/**
 * Get update configuration for a route path
 * Uses precise segment matching to avoid false positives
 *
 * Matching Logic:
 * - Converts route patterns to regex (handles :param placeholders)
 * - Performs exact matching (no partial matches)
 * - Checks more specific routes first (sorted by path segments)
 *
 * Examples:
 * - "/company" matches "company" ✅
 * - "/my-company" does NOT match "company" ✅
 * - "/portal/test-site/inductee/inductions" matches "portal/:seoName/inductee/inductions" ✅
 * - "/portal/test" does NOT match "portal/:seoName/inductee/inductions" ✅
 */
export function getUpdateConfigForRoute(path: string): RouteUpdateConfig | null {
	// Normalize path (remove leading/trailing slashes)
	const normalizedPath = path.replace(/^\/+|\/+$/g, "");

	// Sort configs by specificity (more segments = more specific = check first)
	// This ensures "company-web-portal" is checked before "company"
	const sortedConfigs = [...ROUTE_UPDATE_CONFIG].sort(
		(a, b) => b.path.split("/").length - a.path.split("/").length,
	);

	for (const config of sortedConfigs) {
		const configPath = config.path.replace(/^\/+|\/+$/g, "");

		// Convert route pattern to regex
		// Replace :param with [^/]+ to match any segment value
		// Example: "portal/:seoName/inductee" → "portal/[^/]+/inductee"
		const pattern = configPath.replace(/:[^/]+/g, "[^/]+");
		const regex = new RegExp(`^${pattern}$`);

		if (regex.test(normalizedPath)) {
			return config;
		}
	}

	return null;
}

/**
 * Validate route update configuration
 * Checks for common configuration errors
 *
 * Call this during app initialization to catch config issues early
 */
export function validateRouteUpdateConfig(): void {
	const paths = ROUTE_UPDATE_CONFIG.map((c) => c.path);
	const duplicates = paths.filter((path, index) => paths.indexOf(path) !== index);

	if (duplicates.length > 0) {
		console.error("[RouteUpdateConfig] ERROR: Duplicate paths found:", duplicates);
	}

	// Check for description inconsistencies
	ROUTE_UPDATE_CONFIG.forEach((config) => {
		// Check if description contradicts silentUpdate setting
		if (config.silentUpdate && config.description.toLowerCase().includes("show notification")) {
			console.warn(
				`[RouteUpdateConfig] WARNING: "${config.path}" has silentUpdate: true but description mentions showing notification`,
			);
		}
		if (
			!config.silentUpdate &&
			config.description.toLowerCase().includes("silent update")
		) {
			console.warn(
				`[RouteUpdateConfig] WARNING: "${config.path}" has silentUpdate: false but description mentions silent update`,
			);
		}

		// Check for trailing slashes (should be consistent)
		if (config.path.endsWith("/")) {
			console.warn(
				`[RouteUpdateConfig] WARNING: "${config.path}" has trailing slash - should be removed for consistency`,
			);
		}
	});

	console.log(
		`[RouteUpdateConfig] Validation complete - ${ROUTE_UPDATE_CONFIG.length} routes configured`,
	);
}
